/**
 * CreateWithAi — "Créer avec l'IA…" quick-action.
 *
 * A compact expandable control: click → type what to create → the smart-create
 * resolver navigates to the right CrudPage create route (or the current one),
 * opens its create dialog, and pre-fills it via /ai/copilot/fill-form. Used as a
 * sidebar quick-action; the AuroraCommandBar wires the same `useSmartCreate`
 * hook inline in its command palette.
 */

import * as React from 'react'
import { Wand2, Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { useSmartCreate } from '@/lib/copilot/smart-create'
import { AiCreditBadge, useAiCreditAction } from '@/components/shared/AiCreditBadge'

export interface CreateWithAiProps {
  /** Fired right before navigation/dialog-open (e.g. close the copilot panel). */
  onLaunch?: () => void
  className?: string
}

export function CreateWithAi({ onLaunch, className }: CreateWithAiProps) {
  const [open, setOpen] = React.useState(false)
  const [text, setText] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const smartCreate = useSmartCreate()
  // AI-credit metering for the smart-create resolver (fill-form under the hood).
  const ai = useAiCreditAction({ feature: 'copilot.smart-create', defaultCost: 3 })

  const run = async () => {
    const g = text.trim()
    if (!g) return
    setBusy(true)
    try {
      await ai.run(() => smartCreate(g, onLaunch), { cost: 3, label: 'Créer avec l’IA' })
      setOpen(false)
      setText('')
    } finally {
      setBusy(false)
    }
  }

  if (!open) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={cn('h-7 gap-1.5 rounded-full border-primary/40 text-xs text-primary', className)}
        onClick={() => setOpen(true)}
      >
        <Wand2 className="h-3.5 w-3.5" />
        Créer avec l’IA…
      </Button>
    )
  }

  return (
    <div className={cn('w-full space-y-2 rounded-lg border border-primary/30 bg-primary/5 p-2.5', className)}>
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-xs font-semibold text-primary">
          <Wand2 className="h-3.5 w-3.5" />
          Créer avec l’IA
        </p>
        <div className="flex items-center gap-1.5">
          <AiCreditBadge cost={3} consumed={ai.lastConsumed} />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-5 w-5"
            onClick={() => setOpen(false)}
            aria-label="Fermer"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      </div>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault()
            run()
          }
        }}
        placeholder="Ex : « un risque de fuite de données fournisseur, impact élevé »"
        className="min-h-[60px] bg-background text-xs"
      />
      <div className="flex justify-end">
        <Button size="sm" className="h-7 text-xs" onClick={run} disabled={busy || !text.trim()}>
          {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Wand2 className="mr-1.5 h-3.5 w-3.5" />}
          Créer
        </Button>
      </div>
    </div>
  )
}

export default CreateWithAi
